/**
 * OrderLookup Component
 *
 * Reusable component for looking up existing orders by phone or order number.
 * Handles input formatting, validation, and displays lookup results.
 */

import { useState, useCallback } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { SearchIcon, LoaderIcon } from 'lucide-react';

interface OrderLookupProps {
  onLookup: (searchValue: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

/**
 * Format phone number as user types
 * Example: 8055551234 -> (805) 555-1234
 */
function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, '');

  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

/**
 * Detect if input is a phone number or order number
 */
function detectInputType(value: string): 'phone' | 'order' | 'unknown' {
  const digitsOnly = value.replace(/\D/g, '');

  // Phone number (10 digits)
  if (digitsOnly.length === 10 && /^\d{10}$/.test(digitsOnly)) {
    return 'phone';
  }

  // Order number format: PASO-12345 or ORD-YYYYMMDD-XXXX
  if (/^(PASO|ORD)-[\w\d-]+$/i.test(value)) {
    return 'order';
  }

  return 'unknown';
}

export function OrderLookup({ onLookup, isLoading, error }: OrderLookupProps) {
  const [searchValue, setSearchValue] = useState('');
  const [inputType, setInputType] = useState<'phone' | 'order' | 'unknown'>('unknown');

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // If it looks like a phone number, format it
    if (/^\d/.test(value) || value.includes('(')) {
      const formatted = formatPhoneNumber(value);
      setSearchValue(formatted);
      setInputType(detectInputType(formatted));
    } else {
      // Otherwise, treat as order number
      setSearchValue(value.toUpperCase());
      setInputType(detectInputType(value));
    }
  }, []);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();

    if (!searchValue.trim()) {
      return;
    }

    // Extract raw value for API call
    const rawValue = inputType === 'phone'
      ? searchValue.replace(/\D/g, '')  // Remove formatting for phone
      : searchValue.trim();               // Keep as-is for order number

    onLookup(rawValue);
  }, [searchValue, inputType, onLookup]);

  const isValid = inputType !== 'unknown' && searchValue.trim().length > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Let&apos;s find your container</CardTitle>
        <CardDescription>
          Enter your phone number or order number to look up your existing container
        </CardDescription>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="search-input"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Phone or Order Number
            </label>
            <div className="flex gap-2">
              <div className="flex-1">
                <input
                  id="search-input"
                  type="text"
                  value={searchValue}
                  onChange={handleInputChange}
                  placeholder="(805) 555-1234 or PASO-12345"
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  disabled={isLoading}
                  aria-invalid={!!error}
                  aria-describedby={error ? 'lookup-error' : undefined}
                />
                {searchValue && (
                  <p className="mt-1 text-xs text-gray-500">
                    {inputType === 'phone' && 'Phone number detected'}
                    {inputType === 'order' && 'Order number detected'}
                    {inputType === 'unknown' && 'Enter a valid phone number (10 digits) or order number (PASO-12345)'}
                  </p>
                )}
              </div>
              <Button
                type="submit"
                size="lg"
                disabled={!isValid || isLoading}
                className="px-6"
              >
                {isLoading ? (
                  <>
                    <LoaderIcon className="w-4 h-4 mr-2 animate-spin" />
                    Looking up...
                  </>
                ) : (
                  <>
                    <SearchIcon className="w-4 h-4 mr-2" />
                    Look Up
                  </>
                )}
              </Button>
            </div>
          </div>

          {error && (
            <div
              id="lookup-error"
              className="p-4 bg-red-50 border border-red-200 rounded-md"
              role="alert"
            >
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
}
